import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Loader2, Search, X, Sticker } from "lucide-react";

const GIPHY = "https://api.giphy.com/v1/gifs";

/** GIPHY picker. Their terms require the search call to run in the browser, so the key is
 *  fetched from the backend at runtime instead of being baked into the bundle. */
export default function GifPicker({ onPick, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [offset, setOffset] = useState(0);
  const debounce = useRef(null);

  useEffect(() => {
    api.get("/gifs/config").then(({ data }) => setCfg(data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const fetchGifs = async (term, nextOffset = 0) => {
    if (!cfg?.key) return;
    setLoading(true);
    setError("");
    const path = term.trim()
      ? `${GIPHY}/search?q=${encodeURIComponent(term.trim())}`
      : `${GIPHY}/trending?`;
    try {
      const res = await fetch(
        `${path}&api_key=${cfg.key}&limit=24&offset=${nextOffset}&rating=${cfg.rating}&bundle=messaging_non_clips`,
      );
      if (!res.ok) throw new Error(res.status === 429 ? "GIF search is busy — try again shortly." : "GIF search failed");
      const data = await res.json();
      setItems((prev) => (nextOffset ? [...prev, ...(data.data || [])] : data.data || []));
      setOffset(nextOffset);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cfg?.enabled) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchGifs(q, 0), q ? 400 : 0);
    return () => clearTimeout(debounce.current);
  }, [q, cfg]);

  const choose = (g) => {
    const full = g.images?.downsized_medium || g.images?.downsized || g.images?.fixed_width;
    if (!full?.url) return;
    if (g.analytics?.onclick?.url) fetch(g.analytics.onclick.url).catch(() => {});
    onPick({ gif_url: full.url, gif_id: g.id, title: g.title });
  };

  if (cfg && !cfg.enabled) {
    return (
      <div className="border-t border-white/10 p-4 text-center" data-testid="gif-picker-disabled">
        <Sticker size={18} className="mx-auto text-white/25 mb-2" />
        <p className="text-white/50 text-xs">
          GIF search isn&apos;t switched on yet. You can still post an image from your phone.
        </p>
        <button onClick={onClose} className="mt-2 text-white/40 hover:text-white text-[10px] uppercase tracking-widest">
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-white/10 bg-[#0f0f0f]" data-testid="gif-picker">
      <div className="p-2 flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-[#0a0a0a] border border-white/10 focus-within:border-[#00FF66] rounded-sm px-2">
          <Search size={13} className="text-white/40" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search GIPHY — touchdown, cooked, sad..."
            maxLength={50}
            data-testid="gif-search-input"
            className="flex-1 bg-transparent py-2 outline-none text-sm"
          />
        </div>
        <button onClick={onClose} data-testid="gif-picker-close"
          className="p-2 text-white/40 hover:text-white">
          <X size={15} />
        </button>
      </div>

      {error && <div className="px-3 pb-2 text-[#FF9F0A] text-xs" data-testid="gif-error">{error}</div>}

      <div className="max-h-56 overflow-y-auto px-2 pb-2">
        {loading && items.length === 0 ? (
          <div className="py-8 text-center text-white/40 text-xs inline-flex items-center gap-2 w-full justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading GIFs...
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
            {items.map((g) => (
              <button
                key={g.id}
                onClick={() => choose(g)}
                data-testid={`gif-option-${g.id}`}
                className="relative overflow-hidden rounded-sm border border-white/5 hover:border-[#00FF66] focus:border-[#00FF66] outline-none"
              >
                <img
                  src={g.images?.fixed_width_small?.url || g.images?.fixed_width_small_still?.url}
                  alt={g.title || "GIF"}
                  loading="lazy"
                  className="w-full h-20 object-cover"
                />
              </button>
            ))}
          </div>
        )}
        {items.length > 0 && (
          <button
            onClick={() => fetchGifs(q, offset + 24)}
            disabled={loading}
            data-testid="gif-load-more"
            className="w-full mt-2 py-2 text-[10px] uppercase tracking-widest text-white/50 hover:text-white border border-white/10 rounded-sm"
          >
            {loading ? "Loading..." : "Load more"}
          </button>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-white/5 text-white/30 text-[9px] uppercase tracking-widest text-center">
        Powered by GIPHY
      </div>
    </div>
  );
}
